import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  IconButton,
  Chip,
  Pagination,
  Stack,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { 
  ArrowDropDown as ArrowDropDownIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { userApiLoader } from "../../../action/loaderActions";
import axiosInstance from "../../../utils/axios/axiosInstance";
import Search from "../../../components/common/search/Search";
import { useNavigate } from "react-router-dom";
import loadingGif from '../../../assets/images/loading.gif';

// Loading Overlay Component
const LoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center z-50 bg-white bg-opacity-80">
      <div className="text-center">
        <img
          src={loadingGif}
          alt="Loading..."
          className="w-16 h-16 mx-auto"
        />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading your Listings...
        </Typography>
      </div>
    </div>
  );
};

const AddsTab = () => {
  // State management
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Image loading states
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  
  // Pagination state - Updated limit to 12
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 12
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get loading state from Redux
  const { accessToken } = useSelector((state) => state.user);
  const { isApiLoader } = useSelector((state) => state.loader || { isApiLoader: false });

  // Handle image load start
  const handleImageLoadStart = (adId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [adId]: true
    }));
  };

  // Handle image load complete
  const handleImageLoadComplete = (adId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [adId]: false
    }));
  };

  // Handle image error
  const handleImageError = (adId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [adId]: false
    }));
  };

  // Helper function to get current filter parameter
  const getCurrentFilterParam = () => {
    if (statusFilter === "draft") {
      return 1;
    } else if (statusFilter === "published") {
      return 0;
    } else if (statusFilter === "sold") {
      return 2;
    } else if (statusFilter === "inactive") {
      // For inactive, we'll fetch all and filter client-side
      return null;
    }
    // Return null for default (show all items) - let client-side filtering handle it
    return null;
  };

  // Navigation handlers
  const handleAdClick = (adId) => {
    console.log("Navigating to ad details:", adId);
    navigate(`/myaccount/mylisting/adsdetails/${adId}`);
  };

  const handleEditAd = (adId, event) => {
    event.stopPropagation();
    console.log("Navigating to edit ad:", adId);
    navigate(`/myaccount/mylisting/edit/${adId}`);
  };

  const handleCreateAd = (event) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      console.log("=== CREATE AD BUTTON CLICKED ===");
      console.log("Current location:", window.location.pathname);
      console.log("Navigate function exists:", typeof navigate === 'function');
      console.log("Attempting to navigate to: /myaccount/mylisting/create");

      if (typeof navigate !== 'function') {
        console.error("Navigate function is not available!");
        window.location.href = "/myaccount/mylisting/create";
        return;
      }

      navigate("/myaccount/mylisting/create", { 
        replace: false,
        state: { from: 'ads-tab' }
      });

      console.log("Navigation command executed");

      setTimeout(() => {
        console.log("After navigation - Current location:", window.location.pathname);
      }, 100);

    } catch (error) {
      console.error("Navigation error:", error);
      try {
        window.location.href = "/myaccount/mylisting/create";
      } catch (fallbackError) {
        console.error("Fallback navigation also failed:", fallbackError);
        toast.error("Unable to navigate to create ad page. Please refresh and try again.");
      }
    }
  };

  // API call to get ads list with pagination - FIXED VERSION
  const getAdsList = async (page = 1, filterParam = null) => {
    try {
      dispatch(userApiLoader({ isApiLoader: true }));

      let allAds = [];
      let paginationData = { total: 0, page: 1, limit: 12 };

      if (filterParam === null) {
        // FIXED: When no filter selected, make a single API call without filter parameter
        // This allows the backend to handle pagination properly across all ads
        console.log("Fetching all ads with pagination...");
        
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/user?page=${page}&limit=12&filter=All`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          
          console.log("All ads response:", response.data);
          
          allAds = response.data?.data?.ads || response.data?.ads || [];
          paginationData = response.data?.data?.pagination || { total: 0, page: page, limit: 12 };
          
          console.log(`Single API call results: ${allAds.length} ads on page ${page}`);

        } catch (singleApiError) {
          console.error("Single API call failed, falling back to merged approach:", singleApiError);
          
          // Fallback: If the single API call fails, use the original merged approach
          // but only for page 1, and disable pagination for merged results
          if (page === 1) {
            // Fetch all types separately only for first page
            const publishedResponse = await axiosInstance.get(
              `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/user?page=1&limit=12&filter=0`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            
            const draftResponse = await axiosInstance.get(
              `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/user?page=1&limit=12&filter=1`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            
            const soldResponse = await axiosInstance.get(
              `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/user?page=1&limit=12&filter=2`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            // Merge all ads
            const publishedAds = publishedResponse.data?.data?.ads || publishedResponse.data?.ads || [];
            const draftAds = draftResponse.data?.data?.ads || draftResponse.data?.ads || [];
            const soldAds = soldResponse.data?.data?.ads || soldResponse.data?.ads || [];
            
            allAds = [...publishedAds, ...draftAds, ...soldAds];
            
            // For merged results, set total to match current results to disable pagination
            paginationData = {
              total: allAds.length,
              page: 1,
              limit: allAds.length // This effectively disables pagination
            };

            console.log(`Fallback merged results: ${allAds.length} ads (pagination disabled)`);
          } else {
            // For pages > 1 with merged approach, return empty results
            allAds = [];
            paginationData = { total: 0, page: page, limit: 12 };
          }
        }

      } else {
        // Specific filter selected - use normal API call (this part remains unchanged)
        const queryParams = `page=${page}&limit=12&filter=${filterParam}`;
        console.log("API Query Parameters:", queryParams);

        const response = await axiosInstance.get(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/user?${queryParams}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        allAds = response.data?.data?.ads || response.data?.ads || [];
        paginationData = response.data?.data?.pagination || { total: 0, page: 1, limit: 12 };
      }

      console.log("Final Ads ===>", allAds);
      console.log("Final Pagination Data ===>", paginationData);
      
      // Debug: Show breakdown of all items
      if (allAds.length > 0) {
        const draftCount = allAds.filter(ad => Number(ad.is_draft || 0) === 1).length;
        const publishedCount = allAds.filter(ad => Number(ad.is_draft || 0) === 0 && Number(ad.is_sold || 0) === 0).length;
        const soldCount = allAds.filter(ad => Number(ad.is_sold || 0) === 1).length;
        
        console.log(`Final Breakdown:
          - Total items: ${allAds.length}
          - Draft items: ${draftCount}
          - Published items: ${publishedCount}
          - Sold items: ${soldCount}`);
      }
      
      // Initialize loading states for all ads
      const initialLoadingStates = {};
      allAds.forEach(ad => {
        if (ad.thumbnail) {
          initialLoadingStates[ad.id] = true;
        }
      });
      setImageLoadingStates(initialLoadingStates);
      
      setItems(allAds);
      setPagination(paginationData);
      setCurrentPage(paginationData.page);

      if (allAds.length === 0 && page === 1) {
        toast.info("You haven't created any ads yet");
      }
    } catch (error) {
      console.error("Ads fetch error:", error.response || error);
      toast.error(error.response?.data?.message || "Failed to load your ads");
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  // Handle pagination change
  const handlePageChange = (event, page) => {
    console.log("Page changed to:", page);
    setCurrentPage(page);
    getAdsList(page, getCurrentFilterParam());
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch ads on component mount and when page changes
  useEffect(() => {
    if (accessToken) {
      // For initial load, don't pass any filter to get all items
      getAdsList(currentPage, null);
    }
  }, [accessToken, dispatch]);

  // Search handler
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    getAdsList(1, getCurrentFilterParam());
  };

  // Handle filter changes
  const handleSortChange = (value) => {
    setSortOption(value);
    setCurrentPage(1);
    getAdsList(1, getCurrentFilterParam());
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
    
    // Map status filter to API filter parameter
    let filterParam = null;
    if (value === "draft") {
      filterParam = 1; // filter=1 for draft ads
    } else if (value === "published") {
      filterParam = 0; // filter=0 for published ads
    } else if (value === "sold") {
      filterParam = 2; // filter=2 for sold ads
    } else if (value === "inactive") {
      // For inactive, fetch all ads and filter client-side
      filterParam = null;
    }
    
    getAdsList(1, filterParam);
  };

  // Filter and sort ads (client-side for current page) - FIXED VERSION
  const filteredAds = items
    .filter(
      (ad) =>
        ad.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ad.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((ad) => {
      // Convert to numbers and handle null/undefined cases
      const isDraft = Number(ad.is_draft || 0) === 1;
      const isSold = Number(ad.is_sold || 0) === 1;
      const isActive = Number(ad.status || 0) === 1;
      
      // Debug logging
      if (items.indexOf(ad) === 0) { // Log only for first item to avoid spam
        console.log("Filtering debug:", {
          statusFilter,
          originalValues: { is_draft: ad.is_draft, is_sold: ad.is_sold, status: ad.status },
          convertedValues: { isDraft, isSold, isActive },
          adName: ad.name
        });
      }
      
      if (statusFilter === "published") {
        // Published: not draft AND not sold AND active
        return !isDraft && !isSold && isActive;
      }
      if (statusFilter === "draft") {
        // Draft: is draft
        return isDraft;
      }
      if (statusFilter === "sold") {
        // Sold: is sold (can be draft or not)
        return isSold;
      }
      if (statusFilter === "inactive") {
        // Inactive: not draft AND not sold AND not active
        return !isDraft && !isSold && !isActive;
      }
      // Default (no filter selected) - show all items including drafts
      return true;
    })
    .sort((a, b) => {
      if (sortOption === "newest") {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortOption === "oldest") {
        return new Date(a.created_at) - new Date(b.created_at);
      } else if (sortOption === "priceHigh") {
        return (b.price || 0) - (a.price || 0);
      } else if (sortOption === "priceLow") {
        return (a.price || 0) - (b.price || 0);
      }
      return 0;
    });

  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Get status display info - FIXED VERSION with consistent data type handling
  const getStatusInfo = (ad) => {
    const isDraft = Number(ad.is_draft || 0) === 1;
    const isSold = Number(ad.is_sold || 0) === 1;
    const isActive = Number(ad.status || 0) === 1;
    
    if (isDraft) {
      return { label: "Draft", color: "#ffffff", bgColor: "#df9929" };
    } else if (isSold) {
      return { label: "Sold", color: "#ffffff", bgColor: "#098c09" };
    } else if (isActive) {
      return { label: "Active", color: "#ffffff", bgColor: "#228ddc" };
    } else {
      return { label: "Inactive", color: "#ffffff", bgColor: "#f01839" };
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs market-le">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body relative">
                  {/* Loading Overlay */}
                  <LoadingOverlay isLoading={isApiLoader} />

                  {/* Header Section */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                        <div className="d-flex align-items-center">
                          <h3 style={{ margin: 0, color: '#2c3e50', fontWeight: 600 }}>My Listings</h3>
                          <Chip 
                            label={`${pagination.total} total Listings`} 
                            size="small" 
                            sx={{ ml: 2, bgcolor: '#c942b1', color: 'white' }} 
                          />
                          {currentPage > 1 && (
                            <Chip 
                              label={`Page ${currentPage} of ${totalPages}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ ml: 1, borderColor: '#c942b1', color: '#c942b1' }} 
                            />
                          )}
                        </div>
                        <div className="d-flex align-items-center flex-wrap mb-1">
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateAd}
                            disabled={isApiLoader}
                            sx={{
                              background: 'linear-gradient(45deg, #c942b1, #e91e63)',
                              color: 'white',
                              fontWeight: 600,
                              px: 3,
                              py: 1.2,
                              borderRadius: 2,
                              textTransform: 'none',
                              boxShadow: '0 4px 12px rgba(201, 66, 177, 0.2)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #b8379f, #d81b60)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 16px rgba(201, 66, 177, 0.3)'
                              },
                              '&:disabled': {
                                background: '#e0e0e0',
                                color: '#9e9e9e',
                                transform: 'none',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            Create New Listing
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter Panel - Fixed */}
                  <div className="row filter-panel mt-4">
                    <div className="col-md-4">
                      <div className="serarch-bar">
                        <Search
                          value={searchQuery}
                          handleInputChange={(e) => handleSearch(e.target.value)}
                          placeholder="Search your Listings..."
                        />
                      </div>
                    </div>
                    <div className="col-md-4"></div>
                    <div className="col-md-4">
                      <div className="w-full flex items-center justify-end gap-2">
                        {/* Reset Button */}
                        {(sortOption || statusFilter) && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSortOption("");
                              setStatusFilter("");
                              setCurrentPage(1);
                              // Reset to first page without any filters
                              getAdsList(1, null);
                            }}
                            sx={{
                              minWidth: 'auto',
                              px: 2,
                              py: 1,
                              height: 40,
                              borderColor: '#c942b1',
                              color: '#c942b1',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#b8379f',
                                backgroundColor: 'rgba(201, 66, 177, 0.1)',
                                color: '#b8379f'
                              }
                            }}
                          >
                            Reset
                          </Button>
                        )}
                        
                        <FormControl fullWidth className="filter">
                          <InputLabel 
                            className="custom-sort-label"
                            shrink={true}
                            sx={{
                              color: '#666',
                              '&.Mui-focused': {
                                color: '#c942b1'
                              }
                            }}
                          >
                            Sort By
                          </InputLabel>
                          <Select
                            value={sortOption}
                            onChange={(e) => handleSortChange(e.target.value)}
                            IconComponent={(props) => (
                              <ArrowDropDownIcon {...props} style={{ color: "#c942b1" }} />
                            )}
                            style={{ height: 40 }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <span style={{ 
                                    color: '#666', 
                                    fontSize: '14px'
                                  }}>
                                    Default Order
                                  </span>
                                );
                              }
                              
                              const sortLabels = {
                                newest: "Newest to Oldest",
                                oldest: "Oldest to Newest", 
                                priceHigh: "Price: High to Low",
                                priceLow: "Price: Low to High"
                              };
                              
                              return sortLabels[selected] || selected;
                            }}
                            sx={{
                              '& .MuiSelect-select': {
                                paddingTop: '14px',
                                paddingBottom: '10px'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <span style={{ color: '#666' }}>
                                Default Order
                              </span>
                            </MenuItem>
                            <MenuItem value="newest">Newest to Oldest</MenuItem>
                            <MenuItem value="oldest">Oldest to Newest</MenuItem>
                            <MenuItem value="priceHigh">Price: High to Low</MenuItem>
                            <MenuItem value="priceLow">Price: Low to High</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth className="filter">
                          <InputLabel 
                            className="custom-sort-label"
                            shrink={true}
                            sx={{
                              color: '#666',
                              '&.Mui-focused': {
                                color: '#c942b1'
                              }
                            }}
                          >
                            Status
                          </InputLabel>
                          <Select
                            value={statusFilter}
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                            IconComponent={(props) => (
                              <ArrowDropDownIcon {...props} style={{ color: "#c942b1" }} />
                            )}
                            style={{ height: 40 }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <span style={{ 
                                    color: '#666',
                                    fontSize: '14px'
                                  }}>
                                    All Statuses
                                  </span>
                                );
                              }
                              
                              const statusLabels = {
                                published: "Published",
                                draft: "Draft",
                                sold: "Sold",
                                inactive: "Inactive"
                              };
                              
                              return statusLabels[selected] || selected;
                            }}
                            sx={{
                              '& .MuiSelect-select': {
                                paddingTop: '14px',
                                paddingBottom: '10px'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <span style={{ color: '#666' }}>
                                All Statuses
                              </span>
                            </MenuItem>
                            <MenuItem value="published">
                              <Box display="flex" alignItems="center">
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    bgcolor: '#1976d2', 
                                    mr: 1 
                                  }} 
                                />
                                Published
                              </Box>
                            </MenuItem>
                            <MenuItem value="draft">
                              <Box display="flex" alignItems="center">
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    bgcolor: '#ed6c02', 
                                    mr: 1 
                                  }} 
                                />
                                Draft
                              </Box>
                            </MenuItem>
                            <MenuItem value="sold">
                              <Box display="flex" alignItems="center">
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    color:'#ffffff',
                                    bgcolor: '#2e7d32', 
                                    mr: 1 
                                  }} 
                                />
                                Sold
                              </Box>
                            </MenuItem>
                            <MenuItem value="inactive">
                              <Box display="flex" alignItems="center">
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    bgcolor: '#f01839', 
                                    mr: 1 
                                  }} 
                                />
                                Inactive
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </div>

                 

                  {/* Ads Grid */}
                  <div className="align-items-c">
                    <div className="marketplace-place">
                      <div className="row">
                        {filteredAds.length === 0 ? (
                          <div className="col-12">
                            <Box 
                              textAlign="center" 
                              py={8}
                              sx={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                borderRadius: 3,
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(201, 66, 177, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mx: 'auto',
                                  mb: 3
                                }}
                              >
                                <AddIcon sx={{ fontSize: 40, color: '#c942b1' }} />
                              </Box>
                              
                              <Typography variant="h5" color="textPrimary" gutterBottom fontWeight={600}>
                                No Listings found
                              </Typography>
                              
                              <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                                {searchQuery || statusFilter ? 
                                  "Try adjusting your search or filter criteria" :
                                  "You haven't created any ads yet. Start selling by creating your first ad!"
                                }
                              </Typography>
                              
                              {!searchQuery && !statusFilter && (
                                <Button
                                  variant="contained"
                                  size="large"
                                  startIcon={<AddIcon />}
                                  onClick={handleCreateAd}
                                  sx={{
                                    background: 'linear-gradient(45deg, #c942b1, #e91e63)',
                                    color: 'white',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #b8379f, #d81b60)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 8px 20px rgba(201, 66, 177, 0.3)'
                                    }
                                  }}
                                >
                                  Create Your First Listing
                                </Button>
                              )}
                            </Box>
                          </div>
                        ) : (
                          filteredAds.map((ad) => {
                            const statusInfo = getStatusInfo(ad);
                            const isImageLoading = imageLoadingStates[ad.id] || false;
                            
                            return (
                              <div key={ad.id} className="h-100 col-md-3 mb-4">
                                <div 
                                  className="product-card"
                                  style={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <div className="product-media" onClick={() => handleAdClick(ad.id)}  style={{ position: 'relative' }}>
                                    <div className="product-img" onClick={() => handleAdClick(ad.id)} >
                                      {ad.thumbnail ? (
                                        <>
                                          {/* Image Loader */}
                                          {isImageLoading && (
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                height: "180px",
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#f5f5f5',
                                                zIndex: 1
                                              }}
                                            >
                                              <CircularProgress 
                                                size={30} 
                                                sx={{ color: '#c942b1' }} 
                                              />
                                            </Box>
                                          )}
                                          <img
                                            src={ad.thumbnail}
                                            alt={ad.name}
                                            className="w-100"
                                            style={{ 
                                              height: "180px", 
                                              objectFit: "cover",
                                              backgroundColor: '#f5f5f5',
                                              display: isImageLoading ? 'none' : 'block'
                                            }}
                                            onLoad={() => handleImageLoadComplete(ad.id)}
                                            onError={() => handleImageError(ad.id)}
                                          />
                                        </>
                                      ) : (
                                        <Box
                                          sx={{
                                            height: "180px",
                                            bgcolor: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#9e9e9e'
                                          }}
                                        >
                                          <Typography variant="body2">No Image</Typography>
                                        </Box>
                                      )}
                                    </div>

                                    {/* Status Badge */}
                                    <Chip
                                      label={statusInfo.label}
                                      size="small"
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        bgcolor: statusInfo.bgColor,
                                        color: `#ffffff`,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        zIndex: 9
                                      }}
                                    />

                                    {/* Category Badge */}
                                    {ad.category_name && (
                                      <Chip
                                        label={ad.category_name}
                                        size="small"
                                        sx={{
                                          position: 'absolute',
                                          bottom: 8,
                                          left: 8,
                                          bgcolor: '#c942b1',
                                          color: '#fff',
                                          fontSize: '0.7rem',
                                          fontWeight: '500',
                                          zIndex: 9
                                        }}
                                      />
                                    )}

                                   
                                    
                                  </div>

                                  <div 
                                    className="product-content" 
                                    onClick={() => handleAdClick(ad.id)} 
                                    style={{ 
                                      padding: '16px',
                                      flexGrow: 1,
                                      display: 'flex',
                                      flexDirection: 'column'
                                    }}
                                  >
                                    {/* Views and Date */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Box display="flex" alignItems="center" color="text.secondary">
                                        <VisibilityIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                        <Typography variant="caption">{ad.views || 0}</Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(ad.created_at).toLocaleDateString()}
                                      </Typography>
                                    </Box>

                                    {/* Price */}
                                    <Typography 
                                      variant="h6" 
                                      color="primary" 
                                      fontWeight={700}
                                      sx={{ mb: 1, color: '#c942b1' }}
                                    >
                                      ${ad.price || 0}
                                    </Typography>

                                    {/* Title */}
                                    <Typography 
                                      variant="subtitle1" 
                                      fontWeight={600}
                                      sx={{ 
                                        mb: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: 1.2,
                                        minHeight: '2.4em'
                                      }}
                                    >
                                      {ad.name}
                                    </Typography>

                                    {/* Description */}
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ 
                                        flexGrow: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {ad.description}
                                    </Typography>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pagination Section */}
                  {totalPages > 1 && (
                    <Box 
                      sx={{ 
                        mt: 4, 
                        mb: 2, 
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: 2
                      }}
                    >
                      {/* Pagination Info */}
                      <Typography variant="body2" color="text.secondary">
                        Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} Listings
                      </Typography>
                      
                      {/* Pagination Controls */}
                      <Stack spacing={2} alignItems="center">
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          disabled={isApiLoader}
                          sx={{
                            '& .MuiPaginationItem-root': {
                              color: '#666',
                              '&:hover': {
                                backgroundColor: 'rgba(201, 66, 177, 0.1)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#c942b1',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#b8379f',
                                },
                              },
                            },
                          }}
                          showFirstButton
                          showLastButton
                          size="large"
                        />
                      </Stack>
                    </Box>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>
    </>
  );
};

export default AddsTab;